/**
 * reportBoundaryError
 * Reports an error caught by the top-level AppErrorBoundary to Sentry, loading the SDK
 * lazily. This module intentionally has NO static `@sentry/react` import so the boundary
 * (which lives in the eager critical path) does not drag the Sentry chunk into the initial
 * bundle — Sentry stays lazy-initialised (audit J1/J3).
 */
export function reportBoundaryError(error: unknown, componentStack?: string | null): void {
  // Load + configure Sentry on demand (captureBoundaryError self-inits). This is the only
  // Sentry entry point on the anonymous landing, where the SDK is otherwise never loaded.
  void import('@/lib/sentry')
    .then(({ captureBoundaryError }) => {
      captureBoundaryError(error, componentStack);
    })
    .catch(() => {
      /* reporting is best-effort */
    });
}

/**
 * Report a guard that correctly prevented something, at `warning` level — see captureGuardWarning.
 * Same lazy-import discipline as reportBoundaryError: no static Sentry import in this module.
 */
export function reportGuardWarning(message: string, tags?: Record<string, string>): void {
  void import('@/lib/sentry')
    .then(({ captureGuardWarning }) => {
      captureGuardWarning(message, tags);
    })
    .catch(() => {
      /* reporting is best-effort */
    });
}
